/**
 * 既にある要求と why を直す（設計書 `redesign/why.html` §2 の `update_spec_requirement` /
 * `update_spec_why` 行）。
 *
 * **渡した項目だけを直す。** 1 つ以上が必須で、値が今と変わらなければ 1 行も書かない。立てた
 * 時刻と立てた主体（`raisedAt` / `actor`）は変えない。直した人と前後の値は操作履歴に 1 行だけ
 * 残す（`kind` = `requirement` / `why`。設計書 `redesign/change-history.html` §3.2）。
 *
 * **確定のあとには直さない。** 要求（why は要求を経由して）が結ばれた案件のどれか 1 つでも確定
 * 済みなら `pj-already-merged`、指定の案件に結ばれていなければ `target-not-found` で止める。
 * 確定した案件の背景を後から書き換えると、当時なぜそうしたかではなく、いま思い出せる理由が残る。
 *
 * **根拠は渡したら一覧ごと差し替える。** 古い根拠を消して新しい番号を振る。足し引きの形に
 * しないのは、呼び出し側に差分の管理を持たせないためである。
 *
 * 行の更新と操作履歴の追記は 1 つのスクリプトに囲む。片方だけが入ると、前後の値の記録の無い
 * 変更か、起きていない変更の記録のどちらかが残る。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 既にある要求（実現すること・受入条件）と why（困りごと・変えたい状態・根拠）を、確定前の案件に限って直し、前後の値を操作履歴に残す
 * @graph-connects none
 */
import { z } from 'zod';
import { SPEC_ELEMENT_TABLE_IDS, specElementWhyEvidenceRowSchema, } from '../../contract/elements/index.js';
import { nonEmptyString } from '../../contract/node-schemas.js';
import { evidenceInputSchema, requirementStatementString, } from '../../contract/requirement-input.js';
import { isPjMerged } from './pj-changes.js';
import { buildAppendOperationsSql, insertRowsSql, tableRef, } from './store.js';
import { toInputSchemaRejections } from './write.js';
/**
 * 要求の直す値。直す項目を 1 つ以上渡す。
 *
 * **要求の文の上限の字数はここでは見ない。** 上限より前に書かれた長い文を持つ要求でも、受入条件
 * だけを直す・今と同じ文を渡す直しは通す。上限は文を変えるときだけ `updateRequirement` が
 * 今の文と比べてから検査する（`requirementStatementString`）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 要求を直す入力スキーマ（実現すること・受入条件のうち 1 つ以上）
 * @graph-connects none
 */
export const requirementUpdateInputSchema = z
    .strictObject({
    statement: nonEmptyString().optional(),
    acceptance: nonEmptyString().optional(),
})
    .refine((input) => input.statement !== undefined || input.acceptance !== undefined, {
    message: 'statement / acceptance のうち直す項目を 1 つ以上渡す',
});
/**
 * why の直す値。直す項目を 1 つ以上渡す。根拠は渡すなら 1 件以上。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business why を直す入力スキーマ（困りごと・変えたい状態・根拠の一覧のうち 1 つ以上。根拠は 1 件以上）
 * @graph-connects none
 */
export const whyUpdateInputSchema = z
    .strictObject({
    statement: nonEmptyString().optional(),
    outcome: nonEmptyString().optional(),
    evidences: z.array(evidenceInputSchema).min(1).optional(),
})
    .refine((input) => input.statement !== undefined || input.outcome !== undefined || input.evidences !== undefined, { message: 'statement / outcome / evidences のうち直す項目を 1 つ以上渡す' });
/**
 * 根拠の並びを比べられる形に揃える。観測日時を ISO 8601 のミリ秒つきへ直し、観測日時 → 原典 →
 * 値 の順に並べる。書式（`:30Z` と `:30.000Z`）や並びの違いだけで「変わった」にしないため。
 *
 * @param evidences 根拠の並び
 * @returns 揃えた並び
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 根拠の並びの観測日時の書式と並び順を揃え、見かけの違いで変更扱いにしないようにする
 * @graph-connects none
 */
export function normalizeEvidences(evidences) {
    return evidences
        .map((evidence) => ({
        source: evidence.source,
        observedAt: new Date(evidence.observedAt).toISOString(),
        value: evidence.value,
    }))
        .sort((left, right) => left.observedAt.localeCompare(right.observedAt) ||
        left.source.localeCompare(right.source) ||
        left.value.localeCompare(right.value));
}
/**
 * 結ばれた案件から、指定の案件で直してよいかを見る。
 *
 * @param args 対象の呼び名・対象の番号・指定の案件・結ばれた案件
 * @param changes 案件の変更の読取
 * @returns 拒否の一覧。直してよければ空
 * @graph-connects isPjMerged [calls] 案件が確定済みかの判定
 */
async function checkLinkedPjs(args, changes) {
    if (!args.linked.includes(args.pjNumber)) {
        return [
            {
                reason: 'target-not-found',
                pointer: 'pj_number',
                message: `${args.label} ${String(args.id)} は案件 ${String(args.pjNumber)} に結ばれていない`,
            },
        ];
    }
    for (const pjNumber of args.linked) {
        if (isPjMerged(await changes.listPjChanges(pjNumber))) {
            return [
                {
                    reason: 'pj-already-merged',
                    pointer: 'pj_number',
                    message: `${args.label} ${String(args.id)} は確定済みの案件 ${String(pjNumber)} に結ばれているので直せない`,
                },
            ];
        }
    }
    return [];
}
/**
 * 操作履歴の 1 行を組む。
 * @graph-connects none
 */
function operationRow(args) {
    return {
        opId: args.opId,
        at: args.at,
        actor: args.actor,
        pjNumber: args.pjNumber,
        kind: args.kind,
        targetId: args.targetId,
        before: JSON.stringify(args.before),
        after: JSON.stringify(args.after),
        refOpId: null,
    };
}
/**
 * 対象が無いときの拒否。
 * @graph-connects none
 */
function notFound(label, pointer, id) {
    return [{ reason: 'target-not-found', pointer, message: `${label} ${String(id)} は無い` }];
}
/**
 * 要求の文を変えるときだけ、上限の字数を検査する。今と同じ文なら何も見ない。
 *
 * @param before 今の文
 * @param after 直したあとの文
 * @returns 拒否の一覧。通せば空
 * @graph-connects requirementStatementString [calls] 要求の文の上限の字数の検査
 * @graph-connects toInputSchemaRejections [calls] 形式検査の結果を拒否の一覧へ直す
 */
function checkChangedStatement(before, after) {
    if (after === before)
        return [];
    const checked = requirementStatementString().safeParse(after);
    return checked.success ? [] : toInputSchemaRejections('statement', checked.error);
}
/**
 * 既にある要求を直す。
 *
 * 手順は 6 つである。①入力を形式検査に通す ②要求の実在 ③指定の案件への結びと結ばれた全案件の
 * 確定済み検査 ④文を変えるなら上限の字数の検査 ⑤前後比較（変わらなければ止める）⑥操作履歴の
 * 番号を採って本文の更新と操作履歴の追記を 1 つの単位で書く（dry-run なら ⑤ までで止める）。
 *
 * @param args 案件番号・要求の番号・直す値（形式検査の前の生の値）・検査だけか・直した主体
 * @param deps 置き場・案件の変更の読取・採番・時刻
 * @returns 直した / 変わらない / 検査だけ / 拒否 と前後の値
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 既にある要求の実現すること・受入条件を、確定前の案件に限って直し、前後の値を操作履歴に残す。文を変えるときだけ上限の字数を検査する
 * @graph-connects checkLinkedPjs [calls] 指定の案件への結びと確定済みの検査
 * @graph-connects checkChangedStatement [calls] 文を変えるときの上限の字数の検査
 * @graph-connects operationRow [calls] 操作履歴の 1 行の組み立て
 * @graph-connects toInputSchemaRejections [calls] 形式検査の結果を拒否の一覧へ直す
 */
export async function updateRequirement(args, deps) {
    const parsed = requirementUpdateInputSchema.safeParse(args.input);
    if (!parsed.success) {
        return { outcome: 'rejected', rejections: toInputSchemaRejections('', parsed.error) };
    }
    const current = await deps.store.findRequirement(args.requirementId);
    if (current === null) {
        return {
            outcome: 'rejected',
            rejections: notFound('要求', 'requirement_id', args.requirementId),
        };
    }
    const rejections = await checkLinkedPjs({
        label: '要求',
        id: args.requirementId,
        pjNumber: args.pjNumber,
        linked: await deps.store.listRequirementPjNumbers(args.requirementId),
    }, deps.changes);
    if (rejections.length > 0)
        return { outcome: 'rejected', rejections };
    const before = {
        statement: current.statement,
        acceptance: current.acceptance,
    };
    const after = {
        statement: parsed.data.statement ?? before.statement,
        acceptance: parsed.data.acceptance ?? before.acceptance,
    };
    const statementRejections = checkChangedStatement(before.statement, after.statement);
    if (statementRejections.length > 0) {
        return { outcome: 'rejected', rejections: statementRejections };
    }
    const id = args.requirementId;
    if (JSON.stringify(before) === JSON.stringify(after)) {
        return { outcome: 'unchanged', id, before, after };
    }
    if (args.dryRun)
        return { outcome: 'preview', id, before, after };
    const opId = (await deps.allocateIds('operations', 1)).from;
    await deps.store.updateRequirement({ requirementId: id, ...after }, operationRow({
        opId,
        at: deps.now(),
        actor: args.actor,
        pjNumber: args.pjNumber,
        kind: 'requirement',
        targetId: id,
        before,
        after,
    }));
    return { outcome: 'updated', id, before, after };
}
/**
 * 既にある why を直す。根拠を渡したら一覧ごと差し替える（新しい番号を振る）。
 *
 * 手順は `updateRequirement` と同じで、案件は why を持つ要求を経由して辿る。前後の値の根拠は
 * 観測日時 → 原典 → 値 の順に揃えてから比べる（`normalizeEvidences`）。
 *
 * @param args 案件番号・why の番号・直す値（形式検査の前の生の値）・検査だけか・直した主体
 * @param deps 置き場・案件の変更の読取・採番・時刻
 * @returns 直した / 変わらない / 検査だけ / 拒否 と前後の値
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 既にある why の困りごと・変えたい状態・根拠を、確定前の案件に限って直し、前後の値を操作履歴に残す
 * @graph-connects checkLinkedPjs [calls] 指定の案件への結びと確定済みの検査
 * @graph-connects normalizeEvidences [calls] 根拠の書式と並びを揃える
 * @graph-connects operationRow [calls] 操作履歴の 1 行の組み立て
 * @graph-connects toInputSchemaRejections [calls] 形式検査の結果を拒否の一覧へ直す
 */
export async function updateWhy(args, deps) {
    const parsed = whyUpdateInputSchema.safeParse(args.input);
    if (!parsed.success) {
        return { outcome: 'rejected', rejections: toInputSchemaRejections('', parsed.error) };
    }
    const current = await deps.store.findWhy(args.whyId);
    if (current === null) {
        return { outcome: 'rejected', rejections: notFound('why', 'why_id', args.whyId) };
    }
    const rejections = await checkLinkedPjs({
        label: 'why',
        id: args.whyId,
        pjNumber: args.pjNumber,
        linked: await deps.store.listWhyPjNumbers(args.whyId),
    }, deps.changes);
    if (rejections.length > 0)
        return { outcome: 'rejected', rejections };
    const before = {
        statement: current.statement,
        outcome: current.outcome,
        evidences: normalizeEvidences(current.evidences),
    };
    const after = {
        statement: parsed.data.statement ?? before.statement,
        outcome: parsed.data.outcome ?? before.outcome,
        evidences: parsed.data.evidences === undefined
            ? before.evidences
            : normalizeEvidences(parsed.data.evidences),
    };
    const id = args.whyId;
    if (JSON.stringify(before) === JSON.stringify(after)) {
        return { outcome: 'unchanged', id, before, after };
    }
    if (args.dryRun)
        return { outcome: 'preview', id, before, after };
    const evidencesChanged = JSON.stringify(before.evidences) !== JSON.stringify(after.evidences);
    let evidenceRows = null;
    if (evidencesChanged) {
        const from = (await deps.allocateIds('whyEvidences', after.evidences.length)).from;
        evidenceRows = after.evidences.map((evidence, index) => ({
            whyId: id,
            evidenceId: from + index,
            ...evidence,
        }));
    }
    const opId = (await deps.allocateIds('operations', 1)).from;
    await deps.store.updateWhy({ whyId: id, statement: after.statement, outcome: after.outcome }, evidenceRows, operationRow({
        opId,
        at: deps.now(),
        actor: args.actor,
        pjNumber: args.pjNumber,
        kind: 'why',
        targetId: id,
        before,
        after,
    }));
    return { outcome: 'updated', id, before, after };
}
/**
 * 要求の本文を 1 件読む文。
 *
 * @param config 接続先
 * @returns 投げる `SELECT` 文
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 要求の番号で本文（実現すること・受入条件）を 1 件読む SELECT を組む
 * @graph-connects bigquery.spec_graph_requirements [reads_from, via:requirementId] 要求の本文
 */
export function buildFindRequirementSql(config) {
    return `SELECT requirementId, statement, acceptance
FROM ${tableRef(config, SPEC_ELEMENT_TABLE_IDS.requirements)}
WHERE requirementId = @requirementId
LIMIT 1`;
}
/**
 * 要求が結ばれた案件の番号を読む文。
 *
 * @param config 接続先
 * @returns 投げる `SELECT` 文
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 要求が結ばれた案件の番号を昇順で読む SELECT を組む
 * @graph-connects bigquery.spec_graph_pj_requirements [reads_from, via:requirementId] 案件と要求の結び
 */
export function buildListRequirementPjNumbersSql(config) {
    return `SELECT DISTINCT pjNumber
FROM ${tableRef(config, SPEC_ELEMENT_TABLE_IDS.pjRequirements)}
WHERE requirementId = @requirementId
ORDER BY pjNumber`;
}
/**
 * why の本文を 1 件読む文。
 *
 * @param config 接続先
 * @returns 投げる `SELECT` 文
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business why の番号で本文（困りごと・変えたい状態）を 1 件読む SELECT を組む
 * @graph-connects bigquery.spec_graph_whys [reads_from, via:whyId] why の本文
 */
export function buildFindWhySql(config) {
    return `SELECT whyId, statement, outcome
FROM ${tableRef(config, SPEC_ELEMENT_TABLE_IDS.whys)}
WHERE whyId = @whyId
LIMIT 1`;
}
/**
 * why の今の根拠を読む文。観測日時は RFC 3339（`T...Z`）の文字列で取り出す。
 *
 * @param config 接続先
 * @returns 投げる `SELECT` 文
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business why の番号で今の根拠（原典・観測日時・観測値）を読む SELECT を組む
 * @graph-connects bigquery.spec_graph_why_evidences [reads_from, via:whyId] why の根拠
 */
export function buildListWhyEvidencesSql(config) {
    return `SELECT source, FORMAT_TIMESTAMP('%FT%H:%M:%E*SZ', observedAt, 'UTC') AS observedAt, value
FROM ${tableRef(config, SPEC_ELEMENT_TABLE_IDS.whyEvidences)}
WHERE whyId = @whyId
ORDER BY evidenceId`;
}
/**
 * why を持つ要求が結ばれた案件の番号を読む文。
 *
 * @param config 接続先
 * @returns 投げる `SELECT` 文
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business why を持つ要求を経由して、結ばれた案件の番号を昇順で読む SELECT を組む
 * @graph-connects bigquery.spec_graph_requirement_whys [reads_from, via:whyId] 要求と why の結び
 * @graph-connects bigquery.spec_graph_pj_requirements [reads_from, via:requirementId] 案件と要求の結び
 */
export function buildListWhyPjNumbersSql(config) {
    return `SELECT DISTINCT p.pjNumber AS pjNumber
FROM ${tableRef(config, SPEC_ELEMENT_TABLE_IDS.requirementWhys)} AS rw
JOIN ${tableRef(config, SPEC_ELEMENT_TABLE_IDS.pjRequirements)} AS p USING (requirementId)
WHERE rw.whyId = @whyId
ORDER BY pjNumber`;
}
/**
 * 要求の本文の更新と操作履歴の追記を 1 つのスクリプトに囲む。
 *
 * @param config 接続先
 * @returns 投げるスクリプト
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 要求の本文の UPDATE と操作履歴の INSERT を 1 つのスクリプトに囲んで組む
 * @graph-connects buildAppendOperationsSql [calls] 操作履歴を足す文
 * @graph-connects bigquery.spec_graph_requirements [writes_to, via:requirementId] 要求の本文の置き換え
 * @graph-connects bigquery.spec_graph_operations [writes_to, via:opId] 操作履歴の追記
 */
export function buildUpdateRequirementScript(config) {
    return [
        'BEGIN TRANSACTION;',
        `UPDATE ${tableRef(config, SPEC_ELEMENT_TABLE_IDS.requirements)} SET statement = @statement, acceptance = @acceptance WHERE requirementId = @requirementId;`,
        buildAppendOperationsSql(config),
        'COMMIT TRANSACTION;',
    ].join('\n');
}
/**
 * why の本文の更新・（根拠を渡したら）根拠の入れ替え・操作履歴の追記を 1 つのスクリプトに囲む。
 * **根拠を渡さないときは根拠の文を組まない。**
 *
 * @param config 接続先
 * @param withEvidences 根拠を入れ替えるか
 * @returns 投げるスクリプト
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business why の本文の UPDATE・根拠の DELETE と INSERT・操作履歴の INSERT を 1 つのスクリプトに囲んで組む
 * @graph-connects buildAppendOperationsSql [calls] 操作履歴を足す文
 * @graph-connects insertRowsSql [calls] 根拠を入れる文
 * @graph-connects bigquery.spec_graph_whys [writes_to, via:whyId] why の本文の置き換え
 * @graph-connects bigquery.spec_graph_why_evidences [writes_to, via:whyId] 根拠の入れ替え
 * @graph-connects bigquery.spec_graph_operations [writes_to, via:opId] 操作履歴の追記
 */
export function buildUpdateWhyScript(config, withEvidences) {
    const evidences = tableRef(config, SPEC_ELEMENT_TABLE_IDS.whyEvidences);
    return [
        'BEGIN TRANSACTION;',
        `UPDATE ${tableRef(config, SPEC_ELEMENT_TABLE_IDS.whys)} SET statement = @statement, outcome = @outcome WHERE whyId = @whyId;`,
        ...(withEvidences
            ? [
                `DELETE FROM ${evidences} WHERE whyId = @whyId;`,
                insertRowsSql(evidences, specElementWhyEvidenceRowSchema, 'whyEvidenceRows'),
            ]
            : []),
        buildAppendOperationsSql(config),
        'COMMIT TRANSACTION;',
    ].join('\n');
}
/**
 * BigQuery が返した値を整数に戻す。大きい値は `{ value }` のラッパで返る。
 * @graph-connects none
 */
function toInteger(value) {
    const raw = value !== null && typeof value === 'object' && 'value' in value
        ? value.value
        : value;
    const parsed = typeof raw === 'number' ? raw : Number(String(raw));
    if (!Number.isInteger(parsed)) {
        throw new Error(`spec-graph: 番号が整数で返らなかった: ${String(value)}`);
    }
    return parsed;
}
/**
 * 要求と why を直す経路の置き場の BigQuery 実装。
 *
 * @param config 接続先
 * @returns 6 操作の実装
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 要求と why を直す経路の置き場（本文と結ばれた案件の読取・本文の更新と操作履歴の追記）の BigQuery 実装
 * @graph-connects buildFindRequirementSql [calls] 要求の本文を読む文
 * @graph-connects buildListRequirementPjNumbersSql [calls] 要求が結ばれた案件を読む文
 * @graph-connects buildFindWhySql [calls] why の本文を読む文
 * @graph-connects buildListWhyEvidencesSql [calls] why の根拠を読む文
 * @graph-connects buildListWhyPjNumbersSql [calls] why を持つ要求が結ばれた案件を読む文
 * @graph-connects buildUpdateRequirementScript [calls] 要求を直すスクリプト
 * @graph-connects buildUpdateWhyScript [calls] why を直すスクリプト
 * @graph-connects toInteger [calls] 返った番号の読み直し
 * @graph-connects bigquery.spec_graph_requirements [queries, via:requirementId] 要求の本文の読み書き
 * @graph-connects bigquery.spec_graph_whys [queries, via:whyId] why の本文の読み書き
 * @graph-connects bigquery.spec_graph_why_evidences [queries, via:whyId] 根拠の読み書き
 * @graph-connects bigquery.spec_graph_pj_requirements [reads_from, via:requirementId] 案件と要求の結び
 * @graph-connects bigquery.spec_graph_requirement_whys [reads_from, via:whyId] 要求と why の結び
 * @graph-connects bigquery.spec_graph_operations [writes_to, via:opId] 操作履歴の追記
 */
export function createBigQueryRequirementUpdateStore(config) {
    const location = config.location === undefined ? {} : { location: config.location };
    const select = async (query, params) => {
        const [rows] = await config.client.query({ query, params, ...location });
        return rows;
    };
    const pjNumbersOf = (rows) => rows.map((row) => toInteger(row['pjNumber']));
    return {
        async findRequirement(requirementId) {
            const [row] = await select(buildFindRequirementSql(config), { requirementId });
            if (row === undefined)
                return null;
            return {
                requirementId: toInteger(row['requirementId']),
                statement: String(row['statement']),
                acceptance: String(row['acceptance']),
            };
        },
        async listRequirementPjNumbers(requirementId) {
            return pjNumbersOf(await select(buildListRequirementPjNumbersSql(config), { requirementId }));
        },
        async findWhy(whyId) {
            const [row] = await select(buildFindWhySql(config), { whyId });
            if (row === undefined)
                return null;
            const evidences = await select(buildListWhyEvidencesSql(config), { whyId });
            return {
                whyId: toInteger(row['whyId']),
                statement: String(row['statement']),
                outcome: String(row['outcome']),
                evidences: evidences.map((evidence) => ({
                    source: String(evidence['source']),
                    observedAt: String(evidence['observedAt']),
                    value: String(evidence['value']),
                })),
            };
        },
        async listWhyPjNumbers(whyId) {
            return pjNumbersOf(await select(buildListWhyPjNumbersSql(config), { whyId }));
        },
        async updateRequirement(row, operation) {
            await config.client.query({
                query: buildUpdateRequirementScript(config),
                params: {
                    requirementId: row.requirementId,
                    statement: row.statement,
                    acceptance: row.acceptance,
                    operationRows: JSON.stringify([operation]),
                },
                ...location,
            });
        },
        async updateWhy(row, evidences, operation) {
            await config.client.query({
                query: buildUpdateWhyScript(config, evidences !== null),
                params: {
                    whyId: row.whyId,
                    statement: row.statement,
                    outcome: row.outcome,
                    operationRows: JSON.stringify([operation]),
                    ...(evidences === null ? {} : { whyEvidenceRows: JSON.stringify(evidences) }),
                },
                ...location,
            });
        },
    };
}
/**
 * 要求と why を直す経路の置き場のメモリ実装。
 *
 * @param initial 初期の状態。省略した表は空から始める
 * @returns 6 操作の実装と、保持している状態
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 要求と why を直す経路の置き場のメモリ実装（テストと BigQuery 無しの実行用）
 * @graph-connects none
 */
export function createInMemoryRequirementUpdateStore(initial = {}) {
    const state = {
        requirements: [...(initial.requirements ?? [])],
        whys: [...(initial.whys ?? [])],
        whyEvidences: [...(initial.whyEvidences ?? [])],
        pjRequirements: [...(initial.pjRequirements ?? [])],
        requirementWhys: [...(initial.requirementWhys ?? [])],
        operations: [...(initial.operations ?? [])],
    };
    const pjNumbersOfRequirements = (requirementIds) => [
        ...new Set(state.pjRequirements
            .filter((row) => requirementIds.has(row.requirementId))
            .map((row) => row.pjNumber)),
    ].sort((left, right) => left - right);
    return {
        state,
        store: {
            findRequirement(requirementId) {
                const row = state.requirements.find((item) => item.requirementId === requirementId);
                return Promise.resolve(row === undefined ? null : { ...row });
            },
            listRequirementPjNumbers(requirementId) {
                return Promise.resolve(pjNumbersOfRequirements(new Set([requirementId])));
            },
            findWhy(whyId) {
                const row = state.whys.find((item) => item.whyId === whyId);
                if (row === undefined)
                    return Promise.resolve(null);
                const evidences = state.whyEvidences
                    .filter((evidence) => evidence.whyId === whyId)
                    .sort((left, right) => left.evidenceId - right.evidenceId)
                    .map(({ source, observedAt, value }) => ({ source, observedAt, value }));
                return Promise.resolve({ ...row, evidences });
            },
            listWhyPjNumbers(whyId) {
                const requirementIds = new Set(state.requirementWhys
                    .filter((row) => row.whyId === whyId)
                    .map((row) => row.requirementId));
                return Promise.resolve(pjNumbersOfRequirements(requirementIds));
            },
            updateRequirement(row, operation) {
                state.requirements = state.requirements.map((item) => item.requirementId === row.requirementId
                    ? { ...item, statement: row.statement, acceptance: row.acceptance }
                    : item);
                state.operations.push(operation);
                return Promise.resolve();
            },
            updateWhy(row, evidences, operation) {
                state.whys = state.whys.map((item) => item.whyId === row.whyId
                    ? { ...item, statement: row.statement, outcome: row.outcome }
                    : item);
                if (evidences !== null) {
                    state.whyEvidences = [
                        ...state.whyEvidences.filter((evidence) => evidence.whyId !== row.whyId),
                        ...evidences,
                    ];
                }
                state.operations.push(operation);
                return Promise.resolve();
            },
        },
    };
}
//# sourceMappingURL=requirement-update.js.map